import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { Add, Code, Copy, TickCircle, Trash } from 'iconsax-react';
import chiediConferma from '../../../components/Util/chiediConferma';
import segnalaMancanti from '../../../components/Util/campiMancanti';
import { usePageTitle } from '../../../layouts/PageHeaderExtra';
import { useStatoNelTitolo } from '../../../components/Util/StatoProdotto';
import {
    aggiornaPaginaApp,
    caricaImmaginePagina,
    cronologiaPaginaApp,
    elencoImmaginiPagine,
    leggiPaginaApp,
    urlImmaginePagina,
} from '../../../../api-client/appPlayer';
import { LIST_URL } from './index';
import { ChipDestinatari, destinatari } from './destinatari';

/*
 * Scheda di una pagina dell'app: HTML e CSS, le immagini che le pagine usano e le versioni precedenti.
 * A destra l'anteprima come nel telefono, con o senza Premium. Le modifiche arrivano nell'app entro un
 * minuto; il server toglie script, eventi e link non ammessi. Le regole per chi scrive le pagine sono nel
 * documento degli interventi (cap. 1, «Come sono fatte le pagine»).
 *
 * Il riquadro della Dashboard (`dashboard-premium`) si puo` spegnere: lo stato sta accanto al titolo.
 */

const SPEGNIBILI = ['dashboard-premium', 'dashboard-abbonamento'];
const FONT = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&family=Bebas+Neue&display=swap';

/** Lo stesso documento che l'app costruisce attorno alla pagina, per l'anteprima. */
export const documentoAnteprima = (html, css, premium) => `<!doctype html><html><head>
<meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<base href="${urlImmaginePagina('')}">
<link rel="stylesheet" href="${FONT}">
<style>:root{--spazio-alto:44px;--spazio-basso:34px}body.premium .solo-free,body.free .solo-premium{display:none!important}${REGOLE_IMMAGINI.replace(/\n/g, '')}</style>
<style>${String(css || '').replace(/<\//g, '')}</style>
</head><body class="${premium ? 'premium' : 'free'}">${html || ''}</body></html>`;

// Regole delle immagini, aggiunte al CSS della pagina quando contiene immagini (il server toglie `style`: le proprieta` sono classi).
// Nessuna immagine supera lo schermo; `img-m1` = margini laterali, `img-tondi` = angoli arrotondati, `img-sx|centro|dx` = allineamento.
const REGOLE_IMMAGINI = `img{max-width:100%;height:auto}
img.img-sx{display:block;margin-left:0;margin-right:auto}
img.img-centro{display:block;margin-left:auto;margin-right:auto}
img.img-dx{display:block;margin-left:auto;margin-right:0}
img.img-m1{box-sizing:border-box;max-width:calc(100% - 32px);margin-left:16px;margin-right:16px}
img.img-m1.img-centro{margin-left:auto;margin-right:auto}
img.img-tondi{border-radius:14px}`;
const CSS_IMMAGINI = `\n/* immagini-adattabili */\n${REGOLE_IMMAGINI}\n`;
const conCssImmagini = (html, css) => (/<img/i.test(html || '') && !String(css || '').includes('immagini-adattabili') ? `${css || ''}${CSS_IMMAGINI}` : css);

const dataOra = (iso) => (iso ? new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

export const SchedaPaginaApp = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [pagina, setPagina] = useState(null);
    const [form, setForm] = useState({ title: '', html: '', css: '' });
    const [immagini, setImmagini] = useState([]);
    const [versioni, setVersioni] = useState([]);
    const [premium, setPremium] = useState(false);
    const [errore, setErrore] = useState('');
    const [inCorso, setInCorso] = useState(false);
    const [salvato, setSalvato] = useState(false);
    const [copiato, setCopiato] = useState('');
    const fileImmagine = useRef(null);
    // Anteprima modificabile: si scrive direttamente nel telefono, l'HTML si aggiorna da solo
    const iframe = useRef(null);
    const [srcDoc, setSrcDoc] = useState('');
    const [modaleLink, setModaleLink] = useState(false);
    const [codice, setCodice] = useState(false);
    const [indirizzoLink, setIndirizzoLink] = useState('');
    const premiumAttuale = useRef(false);
    // Immagine scelta nel telefono: riquadro e maniglia per ridimensionarla
    const imgSel = useRef(null);
    const [sel, setSel] = useState(null);
    const ricostruisci = useCallback((html, css) => setSrcDoc(documentoAnteprima(html, css, premiumAttuale.current)), []);

    const caricaVersioni = () => cronologiaPaginaApp(slug).then((r) => setVersioni(r?.error ? [] : r));

    useEffect(() => {
        leggiPaginaApp(slug).then((r) => {
            if (!r || r.error) { setErrore(r?.error || 'Pagina non trovata.'); setPagina({}); return; }
            setPagina(r);
            setForm({ title: r.title || '', html: r.html || '', css: r.css || '' });
            // Le pagine per utenti Premium si vedono come le vede chi ha Premium
            if (destinatari(slug, r.audience) === 'premium') { premiumAttuale.current = true; setPremium(true); }
            ricostruisci(r.html, r.css);
        });
        elencoImmaginiPagine().then((r) => setImmagini(r?.error ? [] : r));
        caricaVersioni();
    }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

    usePageTitle(pagina?.title);
    useEffect(() => {
        if (!salvato) return undefined;
        const t = setTimeout(() => setSalvato(false), 2500);
        return () => clearTimeout(t);
    }, [salvato]);

    useStatoNelTitolo({
        visibile: !!pagina?.slug && SPEGNIBILI.includes(slug),
        attivo: pagina?.active !== false,
        onCambia: async (attivo) => {
            const r = await aggiornaPaginaApp(slug, { active: attivo });
            if (r?.error) return { error: r.error };
            setPagina(r);
            setSalvato(true);
            caricaVersioni();
            return {};
        },
    });

    // Con/senza Premium: cambia solo la classe del body, senza ricaricare l'anteprima (le modifiche fatte a mano restano)
    useEffect(() => {
        premiumAttuale.current = premium;
        const corpo = iframe.current?.contentDocument?.body;
        if (corpo) corpo.className = premium ? 'premium' : 'free';
    }, [premium]);

    // Quando l'anteprima e` caricata si puo` scrivere dentro; ogni modifica riporta l'HTML nel campo
    const anteprimaPronta = () => {
        const doc = iframe.current?.contentDocument;
        if (!doc?.body) return;
        doc.body.contentEditable = 'true';
        doc.body.spellcheck = false;
        doc.addEventListener('input', () => setForm((f) => ({ ...f, html: doc.body.innerHTML })));
        doc.addEventListener('click', (e) => {
            if (e.target.closest?.('a')) e.preventDefault();
            imgSel.current = e.target.tagName === 'IMG' ? e.target : null;
            misura();
        });
        doc.addEventListener('scroll', misura, true);
        doc.defaultView.addEventListener('resize', misura);
        doc.addEventListener('input', misura);
    };
    const misura = useCallback(() => {
        const el = imgSel.current;
        const fr = iframe.current;
        if (!el || !fr || !el.isConnected) { setSel(null); return; }
        const r = el.getBoundingClientRect();
        setSel({ x: fr.offsetLeft + fr.clientLeft + r.left, y: fr.offsetTop + fr.clientTop + r.top, w: r.width, h: r.height, cls: [...el.classList] });
    }, []);
    const sincronizza = () => {
        const doc = iframe.current?.contentDocument;
        if (doc) setForm((f) => ({ ...f, html: doc.body.innerHTML }));
    };
    const larghezzaMax = () => iframe.current?.contentDocument?.body.clientWidth || 300;
    const impostaLarghezza = (px) => {
        const el = imgSel.current;
        if (!el) return;
        el.setAttribute('width', String(Math.round(Math.max(24, Math.min(px, larghezzaMax())))));
        el.removeAttribute('height');
        misura();
    };
    // Proprieta` dell'immagine scelta: allineamento (una sola), margini e angoli (si accendono e spengono)
    const allinea = (nome) => {
        const el = imgSel.current;
        if (!el) return;
        ['img-sx', 'img-centro', 'img-dx'].forEach((c) => el.classList.remove(c));
        el.classList.add(nome);
        misura();
        sincronizza();
    };
    const alterna = (nome) => {
        const el = imgSel.current;
        if (!el) return;
        el.classList.toggle(nome);
        if (!el.getAttribute('class')) el.removeAttribute('class');
        misura();
        sincronizza();
    };
    const rimuoviImmagine = async () => {
        const el = imgSel.current;
        if (!el) return;
        if (!(await chiediConferma({ titolo: 'Rimuovere l\'immagine?', testo: 'L\'immagine sparisce dalla pagina; resta nell\'elenco delle immagini.', conferma: 'Rimuovi' }))) return;
        el.remove();
        imgSel.current = null;
        setSel(null);
        sincronizza();
    };
    const iniziaRidimensiona = (e) => {
        const el = imgSel.current;
        if (!el) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        const x0 = e.clientX;
        const w0 = el.getBoundingClientRect().width;
        const muovi = (ev) => impostaLarghezza(w0 + (ev.clientX - x0));
        const fine = (ev) => {
            ev.currentTarget.removeEventListener('pointermove', muovi);
            ev.currentTarget.removeEventListener('pointerup', fine);
            sincronizza();
        };
        e.currentTarget.addEventListener('pointermove', muovi);
        e.currentTarget.addEventListener('pointerup', fine);
    };
    const comando = (nome, valore) => {
        const doc = iframe.current?.contentDocument;
        if (!doc) return;
        doc.body.focus();
        doc.execCommand(nome, false, valore);
        if (nome === 'insertImage') {
            // L'immagine nuova prende la sua larghezza, ma mai oltre lo schermo; poi si ridimensiona dalla maniglia
            const nuova = [...doc.body.querySelectorAll('img')].filter((i) => !i.hasAttribute('width')).pop();
            if (nuova) {
                const adatta = () => { nuova.setAttribute('width', String(Math.min(nuova.naturalWidth || larghezzaMax(), larghezzaMax()))); nuova.removeAttribute('height'); imgSel.current = nuova; misura(); sincronizza(); };
                if (nuova.complete && nuova.naturalWidth) adatta(); else nuova.addEventListener('load', adatta);
            }
        }
        setForm((f) => ({ ...f, html: doc.body.innerHTML }));
    };
    const inserisciLink = () => {
        if (segnalaMancanti(!indirizzoLink.trim() ? [{ campo: 'pa-link', messaggio: 'Manca l\'indirizzo del link' }] : []) > 0) return;
        setModaleLink(false);
        comando('createLink', indirizzoLink.trim());
        setIndirizzoLink('');
    };
    const cambiato = pagina && (form.title !== (pagina.title || '') || form.html !== (pagina.html || '') || form.css !== (pagina.css || ''));

    const salva = async () => {
        if (segnalaMancanti(!form.title.trim() ? [{ campo: 'pa-titolo', messaggio: 'Manca il nome della pagina' }] : []) > 0) return;
        setInCorso(true);
        const r = await aggiornaPaginaApp(slug, { title: form.title.trim(), html: form.html, css: conCssImmagini(form.html, form.css) });
        setInCorso(false);
        if (r?.error) { setErrore(r.error); return; }
        setPagina(r);
        // Il server toglie quello che non e` ammesso: nell'editor resta la versione salvata davvero
        setForm({ title: r.title || '', html: r.html || '', css: r.css || '' });
        ricostruisci(r.html, r.css);
        setSalvato(true);
        caricaVersioni();
    };

    const caricaImmagine = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setInCorso(true);
        const r = await caricaImmaginePagina(file);
        setInCorso(false);
        if (r?.error) { setErrore(r.error); return; }
        setImmagini((lista) => [...lista.filter((i) => i.fileName !== r.fileName), r].sort((a, b) => a.fileName.localeCompare(b.fileName)));
    };

    const copia = async (testo) => {
        try { await navigator.clipboard.writeText(testo); setCopiato(testo); setTimeout(() => setCopiato(''), 2000); } catch { /* niente appunti */ }
    };

    if (!pagina) return <div className="text-center py-5"><Spinner animation="border" role="status" /></div>;

    return (
        <>
            <Modal show={!!errore} onHide={() => setErrore('')} centered>
                <Modal.Header closeButton><Modal.Title>Controlla i dati</Modal.Title></Modal.Header>
                <Modal.Body>{errore}</Modal.Body>
            </Modal>

            <Modal show={modaleLink} onHide={() => setModaleLink(false)} centered>
                <Modal.Header closeButton><Modal.Title>Inserisci un link</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form.Label htmlFor="pa-link">Indirizzo *</Form.Label>
                    <Form.Control id="pa-link" value={indirizzoLink} onChange={(e) => setIndirizzoLink(e.target.value)} autoFocus />
                    <Form.Text className="text-dark">https://… oppure sportid://premium, sportid://coachbot, sportid://acquista, sportid://chiudi</Form.Text>
                </Modal.Body>
                <Modal.Footer>
                    <Button className="btn btn-light btn-add" onClick={() => setModaleLink(false)}>Annulla</Button>
                    <Button className="btn btn-primary btn-add" onClick={inserisciLink}>Inserisci</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={codice} onHide={() => setCodice(false)} centered size="xl" dialogClassName="sid-modal-codice">
                <Modal.Header closeButton><Modal.Title>Codice HTML e CSS</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Row className="g-4">
                        <Col lg={7}>
                            <Form.Label htmlFor="pa-html">HTML</Form.Label>
                            <Form.Control as="textarea" id="pa-html" className="sid-codice" rows={20} spellCheck={false} maxLength={50000}
                                value={form.html} onChange={(e) => { setForm({ ...form, html: e.target.value }); ricostruisci(e.target.value, form.css); }} />
                            <Form.Text className="text-dark">
                                Solo il contenuto del body. Link: sportid://premium, sportid://coachbot, sportid://acquista, sportid://chiudi
                                o https. Classi solo-premium e solo-free per chi ha o non ha Premium.
                            </Form.Text>
                        </Col>
                        <Col lg={5}>
                            <Form.Label htmlFor="pa-css">CSS</Form.Label>
                            <Form.Control as="textarea" id="pa-css" className="sid-codice" rows={20} spellCheck={false} maxLength={30000}
                                value={form.css} onChange={(e) => { setForm({ ...form, css: e.target.value }); ricostruisci(form.html, e.target.value); }} />
                            <Form.Text className="text-dark">Caratteri Inter e Bebas Neue; spazi delle barre del telefono in var(--spazio-alto) e var(--spazio-basso).</Form.Text>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="justify-content-center">
                    <Button className="btn btn-primary btn-add" onClick={() => setCodice(false)}>Chiudi</Button>
                </Modal.Footer>
            </Modal>

            <Row className="g-4">
                <Col xl={7}>
                    <Card>
                        <Card.Body className="sid-card-body--spaced">
                            <div className="d-flex align-items-end gap-3">
                                <div className="flex-grow-1">
                                    <Form.Label htmlFor="pa-titolo">Nome della pagina * <ChipDestinatari slug={slug} audience={pagina.audience} /></Form.Label>
                                    <Form.Control id="pa-titolo" value={form.title} maxLength={120} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                                </div>
                                <Button type="button" className="btn btn-light sid-btn-compatto mb-1" onClick={() => setCodice(true)}>
                                    <Code size={14} /><span>Codice HTML e CSS</span>
                                </Button>
                            </div>

                            <div className="mt-4" />
                            <div className="sid-barra-editor" role="toolbar" aria-label="Formattazione">
                                {[
                                    ['B', 'Grassetto', () => comando('bold')],
                                    ['I', 'Corsivo', () => comando('italic')],
                                    ['H', 'Titolo', () => comando('formatBlock', 'h2')],
                                    ['¶', 'Paragrafo', () => comando('formatBlock', 'p')],
                                    ['•', 'Elenco', () => comando('insertUnorderedList')],
                                    ['Link', 'Inserisci un link', () => setModaleLink(true)],
                                    ['↶', 'Annulla', () => comando('undo')],
                                    ['↷', 'Ripeti', () => comando('redo')],
                                ].map(([testo, etichetta, azione]) => (
                                    <button key={etichetta} type="button" title={etichetta} aria-label={etichetta}
                                        onMouseDown={(e) => e.preventDefault()} onClick={azione}>{testo}</button>
                                ))}
                                {/* Serve solo se la pagina ha parti diverse per chi ha o non ha Premium */}
                                {/solo-(premium|free)/.test(form.html) && (
                                    <Form.Check className="ms-auto" type="switch" id="pa-premium" label="Con Premium" checked={premium} onChange={(e) => setPremium(e.target.checked)} />
                                )}
                            </div>
                            {sel && (
                                <div className="sid-prop-immagine" role="group" aria-label="Proprietà dell'immagine">
                                    <strong>Immagine</strong>
                                    <span className="sid-prop-immagine__gruppo" aria-label="Larghezza">
                                        {[25, 50, 75, 100].map((p) => (
                                            <button key={p} type="button" className="sid-icona-btn sid-icona-btn--testo"
                                                onMouseDown={(e) => e.preventDefault()} onClick={() => { impostaLarghezza((larghezzaMax() * p) / 100); sincronizza(); }}>{p}%</button>
                                        ))}
                                    </span>
                                    <span className="sid-prop-immagine__gruppo" aria-label="Allineamento">
                                        {[['img-sx', 'Sinistra', '⇤'], ['img-centro', 'Centro', '↔'], ['img-dx', 'Destra', '⇥']].map(([c, t, i]) => (
                                            <button key={c} type="button" title={t} aria-label={t} className={`sid-icona-btn${sel.cls.includes(c) ? ' is-attivo' : ''}`}
                                                onMouseDown={(e) => e.preventDefault()} onClick={() => allinea(c)}>{i}</button>
                                        ))}
                                    </span>
                                    <button type="button" className={`sid-icona-btn sid-icona-btn--testo${sel.cls.includes('img-m1') ? ' is-attivo' : ''}`} title="Margini ai lati: l'immagine non tocca i bordi dello schermo"
                                        onMouseDown={(e) => e.preventDefault()} onClick={() => alterna('img-m1')}>Margini</button>
                                    <button type="button" className={`sid-icona-btn sid-icona-btn--testo${sel.cls.includes('img-tondi') ? ' is-attivo' : ''}`} title="Angoli arrotondati"
                                        onMouseDown={(e) => e.preventDefault()} onClick={() => alterna('img-tondi')}>Angoli</button>
                                    <button type="button" className="sid-icona-btn sid-icona-btn--rosso ms-auto" title="Rimuovi l'immagine" aria-label="Rimuovi l'immagine"
                                        onMouseDown={(e) => e.preventDefault()} onClick={rimuoviImmagine}><Trash size={16} /></button>
                                </div>
                            )}
                            <div className="sid-telefono-centrato">
                                <iframe ref={iframe} title="Pagina dell'app" sandbox="allow-same-origin" srcDoc={srcDoc} onLoad={anteprimaPronta} className="sid-telefono-anteprima__schermo" />
                                {sel && (
                                    <>
                                        <div className="sid-img-sel" style={{ left: sel.x, top: sel.y, width: sel.w, height: sel.h }} />
                                        <div className="sid-img-maniglia" title="Trascina per ridimensionare" style={{ left: sel.x + sel.w - 9, top: sel.y + sel.h - 9 }} onPointerDown={iniziaRidimensiona} />
                                    </>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                    <div className="d-flex justify-content-end gap-4 mt-4">
                        <Button className="btn btn-light" onClick={() => navigate(LIST_URL)}>Annulla</Button>
                        <Button className="btn btn-primary" onClick={salva} disabled={inCorso || !cambiato}>Salva</Button>
                    </div>
                </Col>
                <Col xl={5}>
                    <Card>
                        <Card.Body className="sid-card-body--spaced">
                            <h5 className="sid-form-section">Immagini</h5>
                            <p className="sid-nota-leggibile">
                                Nelle pagine si scrivono con il percorso <strong>appPages/images/nome</strong>. JPG, PNG o WEBP fino a 2 MB;
                                un'immagine con lo stesso nome sostituisce quella di prima, in tutte le pagine.
                            </p>
                            <div className="sid-immagini-pagine">
                                {immagini.map((i) => (
                                    <div key={i.fileName} className="sid-immagini-pagine__voce">
                                        <img src={i.url || urlImmaginePagina(i.src)} alt="" />
                                        <div className="d-flex gap-2 justify-content-center">
                                            <button type="button" className="sid-icona-btn" title="Inserisci nella pagina" aria-label="Inserisci nella pagina" onClick={() => comando('insertImage', i.src)}><Add size={16} /></button>
                                            <button type="button" className="sid-icona-btn" title="Copia percorso" aria-label="Copia percorso" onClick={() => copia(i.src)}>
                                                {copiato === i.src ? <TickCircle size={16} /> : <Copy size={16} />}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                <button type="button" className="sid-galleria__aggiungi" aria-label="Carica immagine" title="Carica immagine" onClick={() => fileImmagine.current?.click()} disabled={inCorso}>
                                    <Add size={28} />
                                </button>
                            </div>
                            <input ref={fileImmagine} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={caricaImmagine} />

                        </Card.Body>
                    </Card>
                    <Card className="mt-4">
                        <Card.Body className="sid-card-body--spaced">
                            <h5 className="sid-form-section">Versioni precedenti</h5>
                            {versioni.length === 0 && <p>Nessuna modifica salvata finora.</p>}
                            {versioni.map((v) => (
                                <div key={v.savedAt} className="d-flex align-items-center gap-3 py-2 sid-riga-versione">
                                    <span>{dataOra(v.updatedAt)}</span>
                                    <span className="ms-auto" />
                                    <Button type="button" className="btn btn-light sid-btn-compatto"
                                        onClick={() => { setForm({ title: v.title || form.title, html: v.html || '', css: v.css || '' }); ricostruisci(v.html, v.css); }}>
                                        Rimetti nell'editor
                                    </Button>
                                </div>
                            ))}
                            {versioni.length > 0 && <Form.Text className="text-dark">«Rimetti nell'editor» carica quella versione: diventa la pagina dell'app solo con Salva.</Form.Text>}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            {salvato && <div className="sid-avviso-salvato" role="status">✓ Modifica salvata</div>}
        </>
    );
};

export default SchedaPaginaApp;
